/**
 * 版面骨架。
 *
 * §7.1 **導覽由伺服器算好給前端**（`/api/auth/me` 的 `nav`），前端不做權限推導。
 *      理由：導覽規則會變，放在後端只要改一個地方；前端只負責畫出來。
 * §7.3 綁定站台的人，導覽只顯示該站台——後端已經處理好了，這裡照畫。
 * §12.6 只屬於一個廠區的人**看不到廠區選擇器**；屬於多廠區的人，
 *       畫面頂端固定顯示目前廠區的名稱，避免在錯的廠區過帳。
 */

import { useEffect, useState } from 'react'
import { useAuth } from './auth/AuthContext.js'
import { LoginScreen } from './auth/LoginScreen.js'
import { ReauthDialog } from './auth/ReauthDialog.js'
import { PasswordChangeScreen } from './auth/PasswordChangeScreen.js'
import { ConfigProvider } from './config/ConfigContext.js'
import { Link, navigate, useRoute } from './router.js'
import { Alert } from './components/ui.js'
import { Screen } from './screens/index.js'

export function App() {
  const { me, loading, logout } = useAuth()
  const { path } = useRoute()
  const [navOpen, setNavOpen] = useState(false)

  useEffect(() => { setNavOpen(false) }, [path])

  // §7.1 home：登入後的落點由伺服器決定（§7.2 各角色不同）
  useEffect(() => {
    if (me && path === '/') navigate(me.home, { replace: true })
  }, [me, path])

  // §5.6 一次性密碼 → 強制改密碼，其他畫面都進不去
  useEffect(() => {
    if (me?.user.mustChangePassword && path !== '/me/password') {
      navigate('/me/password', { replace: true })
    }
  }, [me, path])

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center text-sm text-ink-500">
        載入中…
      </div>
    )
  }

  if (!me) return <><LoginScreen /><ReauthDialog /></>

  if (me.user.mustChangePassword) {
    return (
      <div className="min-h-screen bg-ink-100 p-4 lg:p-8">
        <PasswordChangeScreen />
        <ReauthDialog />
      </div>
    )
  }

  return (
    <ConfigProvider>
      <div className="min-h-screen flex flex-col">
        <Header onToggleNav={() => setNavOpen((v) => !v)} onLogout={() => void logout()} />

        <div className="flex flex-1 min-h-0">
          <nav
            className={`${navOpen ? 'block' : 'hidden'} lg:block w-full lg:w-56 shrink-0
              bg-white border-r border-ink-200 p-3
              lg:sticky lg:top-[calc(var(--header-h)+var(--safe-top))]
              lg:h-[calc(100vh-var(--header-h)-var(--safe-top))] lg:overflow-y-auto`}
          >
            <ul className="space-y-0.5">
              {me.nav.map((n) => {
                const active = path === n.href || (n.href !== '/' && path.startsWith(`${n.href}/`))
                return (
                  <li key={n.key}>
                    <Link
                      to={n.href}
                      className={`block rounded px-2 py-2 text-sm min-h-touch flex items-center ${
                        active ? 'bg-sky-100 text-sky-900 font-medium' : 'text-ink-700 hover:bg-ink-100'}`}
                    >{n.label}</Link>
                  </li>
                )
              })}
            </ul>
            <div className="mt-4 border-t border-ink-200 pt-3">
              <Link
                to="/me/password"
                className="block rounded px-2 py-2 text-sm text-ink-600 hover:bg-ink-100"
              >個人設定</Link>
              <a
                href="/docs/"
                target="_blank"
                rel="noreferrer"
                className="block rounded px-2 py-2 text-sm text-ink-600 hover:bg-ink-100"
              >操作手冊 ↗</a>
            </div>
          </nav>

          <main className={`${navOpen ? 'hidden' : 'block'} lg:block flex-1 min-w-0 p-4 lg:p-6`}>
            <div className="mx-auto max-w-6xl space-y-4">
              <SiteBanner />
              <Screen />
            </div>
          </main>
        </div>

        <ReauthDialog />
      </div>
    </ConfigProvider>
  )
}

function Header({ onToggleNav, onLogout }: { onToggleNav: () => void; onLogout: () => void }) {
  const { me, switchSite } = useAuth()
  if (!me) return null

  return (
    <header className="sticky top-0 z-30 bg-ink-900 text-white shadow pt-[var(--safe-top)]">
      <div className="flex items-center gap-3 px-4 py-2.5">
        <button
          className="lg:hidden rounded border border-ink-600 px-2 py-1 text-sm min-h-touch min-w-touch"
          onClick={onToggleNav}
          aria-label="切換選單"
        >☰</button>

        <Link to={me.home} className="flex items-center gap-2 min-w-0">
          <img
            src="/logo.png"
            alt=""
            width={33}
            height={24}
            className="h-6 w-auto shrink-0"
          />
          <span className="font-bold tracking-tight truncate">PV Regen 追溯系統</span>
        </Link>

        {/*
          §12.6 只屬於一個廠區的人看不到選擇器——廠區對他而言不存在，
          畫面與單廠區時完全一樣。多給一個下拉選單只會製造在錯誤廠區過帳的機會。
        */}
        {me.showSiteSwitcher ? (
          <select
            value={me.site?.siteId ?? ''}
            onChange={(e) => void switchSite(e.target.value)}
            className="ml-2 rounded border border-ink-600 bg-ink-800 px-2 py-1 text-sm"
            aria-label="切換廠區"
          >
            {me.sites.map((s) => (
              <option key={s.siteId} value={s.siteId}>
                {s.name}{s.status !== 'active' ? `（${s.status === 'setup' ? '設定中' : '暫停'}）` : ''}
              </option>
            ))}
          </select>
        ) : me.site ? (
          <span className="ml-2 text-xs text-ink-400 hidden sm:inline">{me.site.name}</span>
        ) : null}

        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-ink-300 hidden sm:inline">
            {me.user.displayName}
            <span className="text-ink-500">（{me.roles.join('、') || '無角色'}）</span>
          </span>
          <button
            onClick={onLogout}
            className="rounded border border-ink-600 px-2.5 py-1 text-sm hover:bg-ink-800"
          >登出</button>
        </div>
      </div>
    </header>
  )
}

/**
 * §12.6「屬於多廠區的人，畫面頂端固定顯示目前廠區的名稱，避免在錯的廠區過帳」。
 * 另外把 §12.2 的許可證到期提醒與廠區狀態一起放在這裡。
 */
function SiteBanner() {
  const { me } = useAuth()
  if (!me) return null

  if (!me.site) {
    return (
      <Alert tone="warn" title="尚未選定廠區">
        {me.sites.length === 0
          ? '您的帳號還沒有被指派到任何廠區。請聯絡系統管理者指派廠區與角色。'
          : '請在右上角選擇要作業的廠區。'}
      </Alert>
    )
  }

  return (
    <>
      {me.showSiteSwitcher && (
        <div className="rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-sm text-sky-900">
          目前廠區：<strong>{me.site.name}</strong>
          <span className="ml-1 text-xs text-sky-700">（{me.site.siteId}）</span>
        </div>
      )}
      {me.site.status === 'setup' && (
        <Alert tone="warn" title="這個廠區還在設定中">
          設定完成度未達 7 項，目前不能進行作業。請到廠區管理把清單補齊並啟用廠區。
        </Alert>
      )}
      {me.site.status === 'suspended' && (
        <Alert tone="warn" title="這個廠區目前暫停營運">
          不能新增作業，但查詢與報表不受影響。
        </Alert>
      )}
      {me.site.permitWarning && (
        <Alert tone="warn" title="處理許可證即將到期">{me.site.permitWarning}</Alert>
      )}
    </>
  )
}
